'use client';

import { getCookies } from '@hyphengroup/cms-components/utils/cookies';
import { TENANT_IDS } from '@hyphengroup/cms-components/utils/tenantIds';
import {
  Button,
  CardBody,
  CardFooter,
  Checkbox,
} from '@material-tailwind/react';
import { GoogleIcon } from 'components/modulab/Icon';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useClient } from 'hooks/useClient';
import { useSearchParams } from 'next/navigation';
import { JSX, useEffect, useState } from 'react';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import { trackingAuthButtonClick } from 'utils/tracking';
import { safeUrl } from 'utils/uri';
import { LoginFormStaticText } from '$/(login)/i18n';
import {
  EmailTypoSuggestion,
  useEmailTypoSuggestion,
} from '$/components/EmailTypoSuggestion/EmailTypoSuggestion';
import { LoaderForm, SubmitButton } from '$/components/LoaderForm';
import { useGoogleLogin, useMagicLink } from '$/modules/auth/hooks';
import { parseIdentityToken } from '$/modules/auth/shared';
import { useClientContext } from '$/modules/context/client';
import { AlphaTextInput } from '$/modules/material-tailwind/AlphaTextInput';

type MagicLinkResponse = {
  email?: string;
};

type Props = {
  authEndpoint: string;
  preLoginEmail?: string;
  staticText: Translated<typeof LoginFormStaticText>;
};

type LogInOrSignUpProps = {
  authEndpoint: string;
  marketingConsent: boolean;
  setMarketingConsent: React.Dispatch<React.SetStateAction<boolean>>;
  staticText: Translated<typeof LoginFormStaticText>;
};

export function LoginForm(props: Props): JSX.Element {
  const { preLoginEmail, staticText } = props;
  const { brand, langPrefix } = useClientContext();
  const [response, setResponse] = useState<MagicLinkResponse>(
    preLoginEmail ? { email: preLoginEmail } : {},
  );
  const searchParams = useSearchParams();
  const [marketingConsent, setMarketingConsent] = useState<boolean>(
    searchParams?.get('consent') === 'true',
  );
  const showConfirmation = Boolean(response?.email);

  // If the user landed here due to an expired token, tags.js may refresh it in
  // the background. Poll for a valid session and reload so the server-side
  // redirectAuthenticated middleware sends the user back.
  //
  // The proxy (src/proxy.ts) only redirects an authenticated user away from
  // /login when the access_token is valid: not expired AND its tid matches the
  // current brand. access_token is httpOnly, so mirror that check against the
  // readable id_token, which is issued together with it and carries the same
  // exp/tid. Reloading on a weaker signal (a future access_token_expires cookie
  // that outlives the access token, or a token from the wrong tenant) loops
  // forever: the proxy keeps rejecting the token while the poller keeps
  // reloading.
  useEffect(() => {
    const MAX_ATTEMPTS = 30;
    let attempts = 0;

    const hasValidSession = () => {
      const idToken = getCookies()['id_token'];
      if (!idToken) return false;
      const claims = parseIdentityToken(idToken);
      return (
        Boolean(claims.exp) &&
        claims.exp * 1000 > Date.now() &&
        claims.tid === TENANT_IDS[brand]
      );
    };

    const interval = setInterval(() => {
      if (++attempts >= MAX_ATTEMPTS) {
        clearInterval(interval);
        return;
      }
      if (hasValidSession()) {
        clearInterval(interval);
        window.location.reload();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [brand]);

  const redirectUri = useClient(() => {
    const searchParams = new URLSearchParams(location.search);
    const fallbackRedirectUri = new URL(`${langPrefix}/`, location.href);

    return (
      safeUrl(
        searchParams?.get('redirect_uri') ?? fallbackRedirectUri.toString(),
        location.href,
      )?.toString() ?? fallbackRedirectUri.toString()
    );
  });
  return (
    <LoaderForm
      onSubmit={useMagicLink(
        (data) => setResponse(data),
        (e) => console.error(e), // todo: error handling
      )}
    >
      {redirectUri ? (
        <input type="hidden" name="redirect_uri" value={redirectUri} />
      ) : null}
      <div hidden={showConfirmation || !redirectUri}>
        <LogInOrSignUp
          staticText={staticText}
          authEndpoint={props.authEndpoint}
          marketingConsent={marketingConsent}
          setMarketingConsent={setMarketingConsent}
        />
      </div>
      {showConfirmation ? (
        <MagicLinkConfirmation
          staticText={staticText}
          email={response.email!}
          onReenter={() => setResponse({})}
          marketingConsent={marketingConsent}
        />
      ) : null}
    </LoaderForm>
  );
}

function LogInOrSignUp(props: LogInOrSignUpProps) {
  const { staticText, marketingConsent, setMarketingConsent } = props;
  const [email, setEmail] = useState<string>();
  const suggestion = useEmailTypoSuggestion();
  return (
    <>
      <CardBody className="flex flex-col gap-4">
        <div className="text-center font-bold text-xl lg:text-2xl 2xl:text-3xl">
          {staticText.loginHeader}
        </div>
        <div className="flex flex-col gap-2 md:gap-4">
          <div>
            <Button
              variant="outlined"
              color="gray"
              fullWidth
              className="flex items-center justify-center gap-1"
              onClick={useGoogleLogin(props.authEndpoint)}
            >
              <GoogleIcon size={3} /> {staticText.continueWithGoogle}
            </Button>
          </div>
          <div className="self-center font-medium text-gray-600 text-base md:text-lg">
            {staticText.or}
          </div>
          <div
            // `invalid` does not bubble; capture it so a submit with the field
            // untouched also ends the neutral state and shows the format error
            onInvalidCapture={() => setEmail((current) => current ?? '')}
          >
            <AlphaTextInput
              name="email"
              type="email"
              placeholder={staticText.enterYourEmail}
              required
              value={email ?? ''}
              // A controlled '' already fails required + pattern; stay neutral until the first edit or submit
              indeterminate={email == null}
              onChange={(e) => {
                setEmail(e.target.value);
                suggestion.clear();
              }}
              onBlur={(e) =>
                suggestion.check(
                  e.currentTarget.value,
                  e.currentTarget.validity.valid,
                )
              }
              onValidate={(state) => {
                if (!state.validity.valid) {
                  return staticText.incorrectEmail;
                }
              }}
            />
            <EmailTypoSuggestion
              suggestion={suggestion}
              onAccept={setEmail}
              staticText={staticText}
            />
          </div>
          <div>
            <SubmitButton
              fullWidth
              color="primary"
              type="submit"
              onClick={() => {
                trackingAuthButtonClick(
                  'Continue with Email',
                  'email',
                  `${document.title}_Continue with Email`,
                  marketingConsent,
                );
              }}
            >
              {staticText.continueWithEmail}
            </SubmitButton>
          </div>
        </div>
        <div className="-ml-3 -mt-3">
          <Checkbox
            name="consent"
            label={staticText.marketingConsent}
            checked={marketingConsent}
            onChange={() => {
              setMarketingConsent(!marketingConsent);
            }}
          />
        </div>
      </CardBody>
      <CardFooter
        divider
        className="text-center font-medium text-text/secondary-lighter text-xs md:text-sm"
      >
        {staticText.loginTermsAndConditions}
      </CardFooter>
    </>
  );
}

function MagicLinkConfirmation(props: {
  email: string;
  onReenter: () => void;
  staticText: Translated<typeof LoginFormStaticText>;
  marketingConsent: boolean;
}): JSX.Element {
  const { staticText, onReenter } = props;
  return (
    <CardBody className="flex flex-col items-stretch gap-4">
      <div className="flex size-17 items-center justify-center self-center rounded-full bg-primary-800">
        <MaterialIcon icon="forward_to_inbox" size={6} />
      </div>
      <h2 className="text-center font-bold text-xl xl:text-2xl 2xl:text-3xl">
        {staticText.magicLinkHeader}
      </h2>
      <div className="text-center font-medium text-base xl:text-lg 2xl:text-xl">
        {t(staticText.magicLinkSent, { email: props.email })}
      </div>
      <div className="flex flex-col gap-2">
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => {
            trackingAuthButtonClick(
              'Reenter Email',
              'email',
              `Check your Email_Re-enter your email`,
              props.marketingConsent,
            );
            onReenter();
          }}
        >
          {staticText.reenterYourEmail}
        </Button>
        <SubmitButton
          color="transparent"
          fullWidth
          size="sm"
          onClick={() => {
            trackingAuthButtonClick(
              'Resend Email',
              'email',
              `Check your Email_Resend email`,
              props.marketingConsent,
            );
          }}
        >
          {staticText.resendEmail}
        </SubmitButton>
      </div>
    </CardBody>
  );
}
